import { Box, Grid } from '@material-ui/core';
import { chain } from 'lodash';
import * as React from 'react';
import { useMemo } from 'react';
import { AnalysisChartLoading } from '../../../AnalysisChartLoading';
import { DomainTypeGq } from '../../../../../graphql/projects/models/DomainTypeGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import { ChartProps } from '../../../AnalysisList';
import { adjustTextColor } from '../../../Palette/adjustTextColor';
import DataTimedOut, { didQueryTimeout } from '../../DataTimedOut';
import Header from './Header';
import Item from './Item';
import LastItem from './LastItem';

export interface BestKeywordsCompetitorsProps extends ChartProps { }

export interface Data {
  projectSearchVolume: number;
  itemAnalysis: GroupAnalysis[];
}

export interface GroupAnalysis {
  type?: DomainTypeGq;
  traffic: number;
  id: number;
  label: string;
  items: ItemAnalysis[];
  moreItemsCount: number;
}

export interface ItemAnalysis {
  traffic: number;
  id: number;
  label: string;
}

const BestKeywordsCompetitors: React.FC<BestKeywordsCompetitorsProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  React.useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: {
          showDomainIds: true,
          showLimitItems: true,
          showTagGroupId: true,
          showTagId: true,
          showCompetitorColor: true,
        },
      })),
    []
  );

  // Fetch data
  const dataQuery = projectsApiService.queries.analysis.useGetBestKeywordCompetitors({
    projectId: project.id,
    domainIds: analysisFilterState?.domainIds,
    limitItems: analysisFilterState?.limitItems,
    tagGroupId: analysisFilterState?.tagGroupId,
    tagId: analysisFilterState?.tagId,
  });

  const dataLoading = !dataQuery.result?.competitorAnalyses && dataQuery.query.loading;

  // Structure data
  const data = useMemo(
    (): Data => ({
      projectSearchVolume: dataQuery?.result?.totalVolume ?? 0,
      itemAnalysis:
        chain(dataQuery?.result?.competitorAnalyses)
          .map(
            (domain): GroupAnalysis => ({
              type: project.domains?.find((d) => d.id === domain.domainId)?.type,
              label: project.domains?.find((d) => d.id === domain.domainId)?.name,
              id: domain.domainId,
              traffic: domain.traffic,
              moreItemsCount: domain.keywordCount - domain.topKeywords.length,
              items: domain.topKeywords.map(
                (k): ItemAnalysis => ({
                  id: k.keywordId,
                  label: k.keyword,
                  traffic: k.traffic,
                })
              ),
            })
          )
          .orderBy((competitor) => competitor.traffic, 'desc')
          .value() ?? [],
    }),
    [dataQuery.result]
  );

  const dataPoints = chain(project.domains)
    .filter(
      (domain) =>
        analysisFilterState?.domainIds.includes(domain.id)
    )
    .map((domain) => ({
      domain,
      domainAnalysis: data?.itemAnalysis?.find((domainAnalysis) => domainAnalysis.id === domain.id),
    }))
    .orderBy([(dp) => dp.domainAnalysis?.traffic ?? 0, (dp) => dp.domain.id], ['desc', 'asc'])
    .value();

  const textColor = adjustTextColor(palette, 0.85, 1);

  if (didQueryTimeout(dataQuery?.query)) {
    return <DataTimedOut />;
  }

  // Render
  if (dataLoading) {
    return <AnalysisChartLoading />;
  }

  return (
    <div data-screenshot-id="chart-loaded">
      <div className="mt-8">
        <Grid
          container
          direction="row"
          justifyContent="space-around"
          alignItems="flex-start"
          spacing={4}
        >
          {dataPoints.map((dataPoint) => {
            return (
              <Grid item xs={3} key={dataPoint.domain.id}>
                <div className="mx-auto pb-4">
                  <Grid container direction="column" justifyContent="center" alignItems="center">
                    <Header
                      palette={palette}
                      type={dataPoint.domain.type}
                      label={dataPoint.domain.name}
                      traffic={dataPoint?.domainAnalysis?.traffic ?? 0}
                      analysisFilterState={analysisFilterState}
                      textColor={textColor}
                    />

                    {dataPoint.domainAnalysis?.items?.map((keyword) => (
                      <Item key={keyword.id} label={keyword.label} traffic={keyword.traffic} />
                    ))}
                    <LastItem
                      count={dataPoint?.domainAnalysis?.moreItemsCount ?? 0}
                      language={language}
                    />
                  </Grid>
                </div>
              </Grid>
            );
          })}
          <Grid item xs={12} />
        </Grid>
      </div>
    </div>
  );
};

export default BestKeywordsCompetitors;
